import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { ArrowLeft, Check, X, Calendar, Users, Building2, Filter } from "lucide-react";
import { format, startOfMonth, endOfMonth, subMonths, parseISO } from "date-fns";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

interface Instructor {
  id: string;
  name: string;
  branch_id: string | null;
  branch_name?: string;
}

interface AttendanceRecord {
  id: string;
  instructor_id: string;
  date: string;
  status: string;
}

interface Branch {
  id: string;
  name: string;
}

const InstructorAttendance = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedBranch, setSelectedBranch] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"mark" | "history">("mark");

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", session.user.id)
        .single();

      if (profile) {
        setUserName(profile.full_name);
      }
    };
    checkAuth();
    loadData();
  }, [navigate]);

  useEffect(() => {
    loadAttendance();
  }, [selectedDate, viewMode]);

  const loadData = async () => {
    // Load branches
    const { data: branchData } = await supabase
      .from("batches")
      .select("id, name")
      .in("name", ["IC Calicut", "IC Bangalore"]);
    
    if (branchData) {
      setBranches(branchData);
    }

    // Load instructors with branch info
    const { data: instructorData, error } = await supabase
      .from("instructors")
      .select("id, name, branch_id")
      .eq("status", "Active")
      .order("name");

    if (error) {
      toast.error("Failed to load instructors");
      return;
    }

    // Map branch names to instructors
    const instructorsWithBranch = (instructorData || []).map(inst => ({
      ...inst,
      branch_name: branchData?.find(b => b.id === inst.branch_id)?.name || "Unassigned"
    }));

    setInstructors(instructorsWithBranch);
  };

  const loadAttendance = async () => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    
    let query = supabase
      .from("instructor_attendance")
      .select("*");

    if (viewMode === "mark") {
      query = query.eq("date", dateStr);
    } else {
      // History mode - get current month's data
      const monthStart = format(startOfMonth(selectedDate), 'yyyy-MM-dd');
      const monthEnd = format(endOfMonth(selectedDate), 'yyyy-MM-dd');
      query = query.gte("date", monthStart).lte("date", monthEnd);
    }

    const { data, error } = await query;

    if (error) {
      toast.error("Failed to load attendance");
      return;
    }

    setAttendance(data || []);
  };

  const markAttendance = async (instructorId: string, status: "Present" | "Absent") => {
    setLoading(true);
    const dateStr = format(selectedDate, 'yyyy-MM-dd');

    try {
      // Check if already marked
      const existing = attendance.find(
        a => a.instructor_id === instructorId && a.date === dateStr
      );

      if (existing) {
        // Update existing
        const { error } = await supabase
          .from("instructor_attendance")
          .update({ status })
          .eq("id", existing.id);

        if (error) throw error;
        toast.success(`Updated to ${status}`);
      } else {
        // Insert new
        const { error } = await supabase
          .from("instructor_attendance")
          .insert({
            instructor_id: instructorId,
            status: status,
            date: dateStr,
          });

        if (error) throw error;
        toast.success(`Marked as ${status}`);
      }

      loadAttendance();
    } catch (error: any) {
      toast.error(error.message || "Failed to mark attendance");
    } finally {
      setLoading(false);
    }
  };

  const filteredInstructors = useMemo(() => {
    if (selectedBranch === "all") return instructors;
    if (selectedBranch === "unassigned") return instructors.filter(i => !i.branch_id);
    return instructors.filter(i => i.branch_id === selectedBranch);
  }, [instructors, selectedBranch]);

  const getAttendanceForInstructor = (instructorId: string, date?: string) => {
    const targetDate = date || format(selectedDate, 'yyyy-MM-dd');
    return attendance.find(a => a.instructor_id === instructorId && a.date === targetDate);
  };

  // Calculate stats
  const stats = useMemo(() => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const todayAttendance = attendance.filter(a => a.date === dateStr);
    const present = todayAttendance.filter(a => a.status === "Present").length;
    const absent = todayAttendance.filter(a => a.status === "Absent").length;
    const unmarked = filteredInstructors.length - todayAttendance.filter(
      a => filteredInstructors.some(i => i.id === a.instructor_id)
    ).length;

    return { present, absent, unmarked, total: filteredInstructors.length };
  }, [attendance, filteredInstructors, selectedDate]);

  // Monthly stats for history view
  const monthlyStats = useMemo(() => {
    if (viewMode !== "history") return null;
    
    const stats = filteredInstructors.map(instructor => {
      const instructorAttendance = attendance.filter(a => a.instructor_id === instructor.id);
      const presentDays = instructorAttendance.filter(a => a.status === "Present").length;
      const absentDays = instructorAttendance.filter(a => a.status === "Absent").length;
      const totalDays = presentDays + absentDays;
      const percentage = totalDays > 0 ? Math.round((presentDays / totalDays) * 100) : 0;
      
      return {
        ...instructor,
        presentDays,
        absentDays,
        totalDays,
        percentage
      };
    });

    return stats;
  }, [attendance, filteredInstructors, viewMode]);

  return (
    <Layout userName={userName}>
      <div className="max-w-5xl mx-auto space-y-4">
        <Button
          variant="ghost"
          onClick={() => navigate("/dashboard")}
          className="mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        {/* Header with Controls */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <CardTitle className="text-xl">Instructor Attendance</CardTitle>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={viewMode === "mark" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setViewMode("mark")}
                >
                  Mark Attendance
                </Button>
                <Button
                  variant={viewMode === "history" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setViewMode("history")}
                >
                  Monthly Summary
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              {/* Date Picker */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full sm:w-auto justify-start">
                    <Calendar className="h-4 w-4 mr-2" />
                    {viewMode === "mark" 
                      ? format(selectedDate, "PPP")
                      : format(selectedDate, "MMMM yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              {/* Branch Filter */}
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <Building2 className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by branch" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches.map(branch => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Stats Summary */}
        {viewMode === "mark" && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card className="p-3">
              <div className="text-sm text-muted-foreground">Total</div>
              <div className="text-2xl font-bold">{stats.total}</div>
            </Card>
            <Card className="p-3 border-green-200 bg-green-50 dark:bg-green-950/20">
              <div className="text-sm text-green-700 dark:text-green-400">Present</div>
              <div className="text-2xl font-bold text-green-700 dark:text-green-400">{stats.present}</div>
            </Card>
            <Card className="p-3 border-red-200 bg-red-50 dark:bg-red-950/20">
              <div className="text-sm text-red-700 dark:text-red-400">Absent</div>
              <div className="text-2xl font-bold text-red-700 dark:text-red-400">{stats.absent}</div>
            </Card>
            <Card className="p-3 border-amber-200 bg-amber-50 dark:bg-amber-950/20">
              <div className="text-sm text-amber-700 dark:text-amber-400">Unmarked</div>
              <div className="text-2xl font-bold text-amber-700 dark:text-amber-400">{stats.unmarked}</div>
            </Card>
          </div>
        )}

        {/* Mark Attendance View */}
        {viewMode === "mark" && (
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2">
                {filteredInstructors.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No instructors found for the selected branch
                  </p>
                ) : (
                  filteredInstructors.map((instructor, index) => {
                    const record = getAttendanceForInstructor(instructor.id);
                    return (
                      <div
                        key={instructor.id}
                        className={cn(
                          "flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 border rounded-lg gap-3",
                          record?.status === "Present" && "bg-green-50 dark:bg-green-950/20 border-green-200",
                          record?.status === "Absent" && "bg-red-50 dark:bg-red-950/20 border-red-200"
                        )}
                      >
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm sm:text-base">
                              {instructor.name}
                            </span>
                            {record && (
                              <Badge 
                                variant={record.status === "Present" ? "default" : "destructive"}
                                className="text-xs"
                              >
                                {record.status}
                              </Badge>
                            )}
                          </div>
                          <Badge variant="outline" className="w-fit text-xs">
                            <Building2 className="h-3 w-3 mr-1" />
                            {instructor.branch_name}
                          </Badge>
                        </div>
                        <div className="flex gap-2 w-full sm:w-auto">
                          <Button
                            size="sm"
                            onClick={() => markAttendance(instructor.id, "Present")}
                            disabled={loading}
                            variant={record?.status === "Present" ? "default" : "outline"}
                            className={cn(
                              "flex-1 sm:flex-none",
                              record?.status === "Present" 
                                ? "bg-green-600 hover:bg-green-700" 
                                : "hover:bg-green-100 hover:text-green-700"
                            )}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Present
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => markAttendance(instructor.id, "Absent")}
                            disabled={loading}
                            variant={record?.status === "Absent" ? "destructive" : "outline"}
                            className={cn(
                              "flex-1 sm:flex-none",
                              record?.status !== "Absent" && "hover:bg-red-100 hover:text-red-700"
                            )}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Absent
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Monthly Summary View */}
        {viewMode === "history" && monthlyStats && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                Attendance Summary - {format(selectedDate, "MMMM yyyy")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Instructor</th>
                      <th className="text-left p-2">Branch</th>
                      <th className="text-center p-2">Present</th>
                      <th className="text-center p-2">Absent</th>
                      <th className="text-center p-2">Total</th>
                      <th className="text-center p-2">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyStats.map(stat => (
                      <tr key={stat.id} className="border-b hover:bg-muted/50">
                        <td className="p-2 font-medium">{stat.name}</td>
                        <td className="p-2">
                          <Badge variant="outline" className="text-xs">
                            {stat.branch_name}
                          </Badge>
                        </td>
                        <td className="p-2 text-center text-green-600 font-medium">
                          {stat.presentDays}
                        </td>
                        <td className="p-2 text-center text-red-600 font-medium">
                          {stat.absentDays}
                        </td>
                        <td className="p-2 text-center">{stat.totalDays}</td>
                        <td className="p-2 text-center">
                          <Badge 
                            variant={stat.percentage >= 80 ? "default" : stat.percentage >= 60 ? "secondary" : "destructive"}
                          >
                            {stat.percentage}%
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {monthlyStats.length === 0 && (
                <p className="text-center text-muted-foreground py-8">
                  No instructors found for the selected branch
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default InstructorAttendance;
